import dayjs from 'dayjs';
import { Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';

/**
 * Sanitizes a search input string to guard against:
 * 1. URL Path Traversal / Route breaking characters ('/', '\', '?', '#', '%')
 * 2. SQL injection patterns (quotes, semicolons, comments '--', '/*')
 * 3. Invisible ASCII control characters
 * 4. Excessive input length
 *
 * @param term Raw input term of any type
 * @param maxLength Maximum allowed length (default: 50)
 * @returns Clean, trimmed string safe for URLs and search queries
 */
export function sanitizeSearchTerm(term: unknown, maxLength: number = 50): string {
  if (term == null) {
    return '';
  }

  let sanitized: string;
  if (typeof term === 'string') {
    sanitized = term;
  } else if (typeof term === 'number' || typeof term === 'boolean') {
    sanitized = String(term);
  } else {
    return '';
  }

  // 1. Strip control characters (Unicode category 'Cc')
  sanitized = sanitized.replace(/\p{Cc}/gu, '');

  // 2. Strip multi-line comments and single-line SQL comments
  sanitized = sanitized.replace(/\/\*[\s\S]*?\*\//g, '');
  sanitized = sanitized.replace(/--+/g, '');

  // 3. Strip SQL injection quotes, backticks, semicolons, and URL-breaking delimiters
  // Slashes (/ and \), query delimiter (?), fragment (#), percent (%)
  sanitized = sanitized.replace(/['"`\\/?#%;]/g, '');

  // 4. Collapse consecutive whitespace to a single space and trim
  sanitized = sanitized.replace(/\s+/g, ' ').trim();

  // 5. Enforce length cap
  if (maxLength > 0 && sanitized.length > maxLength) {
    sanitized = sanitized.slice(0, maxLength).trim();
  }

  return sanitized;
}

/**
 * Sanitizes a term and safely encodes it for embedding into URL path segments.
 *
 * Example: `api/search/${safeEncodePathParam(term)}`
 *
 * @param term Raw input term
 * @param maxLength Maximum allowed length (default: 50)
 * @returns URI-encoded sanitized string
 */
export function safeEncodePathParam(term: unknown, maxLength: number = 50): string {
  const sanitized = sanitizeSearchTerm(term, maxLength);
  return sanitized ? encodeURIComponent(sanitized) : '';
}

/**
 * Validates a table sort column against an allowlist.
 * If the provided column is not in the allowlist, returns the fallback default column.
 *
 * Prevents dynamic sort injection in REST paths like `/:page/:limit/:sort/:direction`.
 *
 * @param sort The requested sort column name
 * @param allowedColumns Array or Set of permitted column names
 * @param defaultColumn Safe fallback column name
 */
export function validateSortParam<T extends string>(
  sort: string | null | undefined,
  allowedColumns: readonly T[] | Set<T>,
  defaultColumn: T
): T {
  if (!sort || typeof sort !== 'string') {
    return defaultColumn;
  }

  const cleanSort = sort.trim();
  if (allowedColumns instanceof Set) {
    return allowedColumns.has(cleanSort as T) ? (cleanSort as T) : defaultColumn;
  }

  return (allowedColumns as readonly string[]).includes(cleanSort)
    ? (cleanSort as T)
    : defaultColumn;
}

/**
 * Validates a sort direction to guarantee it is strictly 'asc' or 'desc'.
 *
 * @param dir Sort direction string
 * @param defaultDir Fallback direction (default: 'asc')
 */
export function validateSortDirection(
  dir: string | null | undefined,
  defaultDir: 'asc' | 'desc' = 'asc'
): 'asc' | 'desc' {
  if (!dir || typeof dir !== 'string') {
    return defaultDir;
  }

  const normalized = dir.toLowerCase().trim();
  return normalized === 'asc' || normalized === 'desc' ? normalized : defaultDir;
}

/**
 * Defensive date formatter that handles null, undefined, invalid date strings, and objects.
 * Prevents serializing "Invalid Date" into API URL paths or query params.
 *
 * @param date Input date (string, number, Date, null, undefined)
 * @param format Desired dayjs format string (default: 'DD-MMM-YYYY')
 * @param fallback Return value when date is invalid or empty (default: '')
 */
export function safeFormatDate(
  date: unknown,
  format: string = 'DD-MMM-YYYY',
  fallback: string = ''
): string {
  if (date == null || date === '') {
    return fallback;
  }

  const parsed = dayjs(date as string | number | Date | dayjs.Dayjs);
  if (!parsed.isValid()) {
    return fallback;
  }

  return parsed.format(format);
}

/**
 * RxJS pipeable operator that debounces search input, sanitizes it, and discards duplicate values.
 *
 * Usage:
 * ```ts
 * this.searchControl.valueChanges.pipe(
 *   sanitizeSearchStream(400)
 * ).subscribe(cleanTerm => this.performSearch(cleanTerm));
 * ```
 */
export function sanitizeSearchStream(debounceMs: number = 300, maxLength: number = 50) {
  return (source$: Observable<string | null | undefined>): Observable<string> => {
    return source$.pipe(
      debounceTime(debounceMs),
      map((val) => sanitizeSearchTerm(val, maxLength)),
      distinctUntilChanged()
    );
  };
}
